import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ResourceList,
  ResourceItem,
  Text,
  InlineStack,
  Box,
  Spinner,
} from '@shopify/polaris';
import { api } from '../utils/api';
import { getStatusBadge, formatRelativeTime, getCarrierName } from '../utils/helpers';

interface Shipment {
  id: string;
  trackingNumber: string;
  carrier: string;
  status: string;
  orderNumber?: string;
  createdAt: string;
}

interface ShipmentsResponse {
  shipments: Shipment[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export default function RecentShipments() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery<ShipmentsResponse>({
    queryKey: ['recent-shipments'],
    queryFn: () => api.get('/shipments', { limit: 5 }),
  });

  const shipments = data?.shipments ?? [];

  if (isLoading) {
    return (
      <Box padding="400">
        <InlineStack align="center">
          <Spinner size="small" />
        </InlineStack>
      </Box>
    );
  }

  if (shipments.length === 0) {
    return (
      <Box padding="400">
        <Text as="p" tone="subdued" alignment="center">
          Aucune expédition récente
        </Text>
      </Box>
    );
  }

  return (
    <ResourceList
      items={shipments}
      renderItem={(shipment) => (
        <ResourceItem
          id={shipment.id}
          onClick={() => navigate('/shipments/' + shipment.id)}
        >
          <InlineStack align="space-between" blockAlign="center">
            <Box>
              <Text as="span" variant="bodyMd" fontWeight="semibold">
                {shipment.trackingNumber}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {getCarrierName(shipment.carrier)}
                {shipment.orderNumber ? ' - ' + shipment.orderNumber : ''}
              </Text>
            </Box>
            <InlineStack gap="300" blockAlign="center">
              {getStatusBadge(shipment.status)}
              <Text as="span" variant="bodySm" tone="subdued">
                {formatRelativeTime(shipment.createdAt)}
              </Text>
            </InlineStack>
          </InlineStack>
        </ResourceItem>
      )}
    />
  );
}
